import { useRef, useCallback } from "react";

interface RateLimitOptions {
  maxAttempts: number;
  windowMs: number;
}

// Simple client-side rate limiter hook
export function useRateLimit(options: RateLimitOptions = { maxAttempts: 5, windowMs: 60000 }) {
  const attemptsRef = useRef<number[]>([]);

  const checkRateLimit = useCallback(() => {
    const now = Date.now();
    // Remove expired attempts
    attemptsRef.current = attemptsRef.current.filter(
      (timestamp) => now - timestamp < options.windowMs
    );
    
    if (attemptsRef.current.length >= options.maxAttempts) {
      const oldestAttempt = attemptsRef.current[0];
      const waitTime = Math.ceil((options.windowMs - (now - oldestAttempt)) / 1000);
      return { allowed: false, waitTime };
    }
    
    return { allowed: true, waitTime: 0 };
  }, [options.maxAttempts, options.windowMs]);

  const recordAttempt = useCallback(() => {
    attemptsRef.current.push(Date.now());
  }, []);

  const reset = useCallback(() => {
    attemptsRef.current = [];
  }, []);

  return { checkRateLimit, recordAttempt, reset };
}

// Debounce hook for booking creation
export function useDebounce<T extends (...args: any[]) => any>(
  callback: T,
  delay: number
): (...args: Parameters<T>) => void {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastCallRef = useRef<number>(0);

  return useCallback(
    (...args: Parameters<T>) => {
      const now = Date.now();
      
      // If called within the delay window, ignore
      if (now - lastCallRef.current < delay) {
        return;
      }
      
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      
      timeoutRef.current = setTimeout(() => {
        lastCallRef.current = Date.now();
        callback(...args);
      }, 100); // Small debounce to prevent double-clicks
    },
    [callback, delay]
  );
}

// Throttle hook for dashboard refresh
export function useThrottle<T extends (...args: any[]) => any>(
  callback: T,
  delay: number
): (...args: Parameters<T>) => void {
  const lastCallRef = useRef<number>(0);
  const pendingRef = useRef<boolean>(false);

  return useCallback(
    (...args: Parameters<T>) => {
      const now = Date.now();
      
      if (now - lastCallRef.current >= delay) {
        lastCallRef.current = now;
        callback(...args);
        pendingRef.current = false;
      } else if (!pendingRef.current) {
        pendingRef.current = true;
        setTimeout(() => {
          lastCallRef.current = Date.now();
          callback(...args);
          pendingRef.current = false;
        }, delay - (now - lastCallRef.current));
      }
    },
    [callback, delay]
  );
}
